import { useEffect, useState, type FC } from 'react';
import { Select, Space, Switchbox, Text } from '@g2u/ui-kit';
import { DefaultOptionType } from 'antd/es/select';
import { observer } from 'mobx-react-lite';
import { Form } from 'antd';
import cx from 'classnames';

import { type JsonDataItem, type WithOptionalDataTest, PaymentMethod } from 'types';
import { useRequestCreditCard } from '../hooks/useRequestCreditCard';
import { usePaymentMethod } from '../hooks/usePaymentMethod';
import { CustomerSearchModal, NotificationsFields, Section } from 'components';
import { useText } from 'helpers/hooks/useText';
import { BookingStore } from 'store';
import styles from '../payment.module.scss';

type Props = WithOptionalDataTest;

export const ConfirmPayment: FC<Props> = observer(() => {
  const { serviceType, productSelection, customer, setPaymentInfo, setPaymentMethod, isNewCustomer } = BookingStore;
  const { getText } = useText();
  const { defaultPaymentMethod, availablePaymentMethods, paymentInfo, cardDetails, isPaymentInfoLoading } =
    usePaymentMethod(customer?.ID, productSelection?.serviceType, serviceType);
  const { requestCreditCard, isRequestSending, initialValues, emailOptions, smsOptions } =
    useRequestCreditCard(customer);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | undefined>(defaultPaymentMethod);
  // ts-expect-error temp
  const { card_name, card_number, card_expiry, cardholder, is_card_valid } = cardDetails;
  const [thirdPartyInvoiceEnabled, setThirdPartyInvoiceEnabled] = useState<boolean>(false);
  const [showSearchCustomerModal, setShowSearchCustomerModal] = useState<boolean>(false);
  const [thirdPartyList, setThirdPartyList] = useState<DefaultOptionType[]>();
  const form = Form.useFormInstance();

  const hasCard = !!card_number;
  const showCardDetails = selectedMethod === PaymentMethod.CreditCard && hasCard && !isNewCustomer;
  const showNoCardMessage = selectedMethod === PaymentMethod.CreditCard && !hasCard;

  useEffect(() => {
    paymentInfo && setPaymentInfo(paymentInfo);
  }, [paymentInfo]);

  useEffect(() => {
    defaultPaymentMethod && setSelectedMethod(defaultPaymentMethod);
  }, [defaultPaymentMethod]);

  return (
    <Section title="Confirm Payment" loading={isPaymentInfoLoading}>
      <Switchbox.$
        name="hasThirdParty"
        initialValue={thirdPartyInvoiceEnabled}
        label="Invoice to the 3rd party"
        onChange={(val) => setThirdPartyInvoiceEnabled(val)}
      />
      {thirdPartyInvoiceEnabled ? (
        <>
          <div
            onClick={() => setShowSearchCustomerModal(true)}
            className={cx(styles.payment__thirdParty, styles.payment__formItem_vertical)}
          >
            <Select.$
              required
              rules={[{ required: true, message: 'Please select' }]}
              name="thirdPartyId"
              options={thirdPartyList}
              readonly
              label="Invoice to:"
            />
          </div>
          <CustomerSearchModal
            customer={null}
            isOpen={showSearchCustomerModal}
            modalTitle={'Search Customer Files'}
            actions={[
              {
                title: 'Select',
                action: (record: any) => {
                  setShowSearchCustomerModal(false);
                  setThirdPartyList([
                    {
                      value: record.ID,
                      label: `#${record.ID} ${record.name.full_name}`,
                    },
                  ]);
                  form.setFieldValue('thirdPartyId', record.ID);
                },
              },
            ]}
            onOk={() => setShowSearchCustomerModal(false)}
            onCancel={() => setShowSearchCustomerModal(false)}
            emptyContent={() => null}
          />
        </>
      ) : (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          <Space.$ className="script-box">
            {hasCard ? (
              <Text.$ className="script-box" size={Text.Size.MD} mark data-dd-privacy="mask">
                {getText('booking.payment.cardOnFile')}
              </Text.$>
            ) : (
              <Text.$ size={Text.Size.MD} mark>
                {getText('booking.payment.paymentInfo')}
              </Text.$>
            )}
          </Space.$>
          <Select.$
            name="paymentMethod"
            placeholder="Select..."
            fullWidth
            required
            rules={[{ required: true }]}
            label="Payment Method:"
            initialValue={defaultPaymentMethod}
            options={availablePaymentMethods?.map((method: JsonDataItem) => {
              return {
                label: method.name,
                value: method.ID,
              };
            })}
            className={cx(
              styles.payment__confirmPayment,
              styles.payment__formItem_horizontal,
              styles.payment__formItem_left,
            )}
            onChange={(val) => {
              setSelectedMethod(val);
              setPaymentMethod(val);
            }}
          />
          {showCardDetails && (
            <>
              <div className={styles.payment__cardDetails}>
                <Text.$>Card Type:</Text.$>
                <Text.$ type="secondary" data-dd-privacy="mask">
                  {card_name}
                </Text.$>
                <Text.$>Card No:</Text.$>
                <Text.$ type="secondary" data-dd-privacy="mask">
                  {'*'.repeat(13) + card_number.slice(-3)}
                </Text.$>
                <Text.$>Expiry Date:</Text.$>
                <Text.$
                  type="secondary"
                  data-dd-privacy="mask"
                >{`${card_expiry.slice(0, 2)}/${card_expiry.slice(2)}`}</Text.$>
                <Text.$>Card Holder:</Text.$>
                <Text.$ type="secondary" data-dd-privacy="mask">
                  {cardholder}
                </Text.$>
              </div>

              {is_card_valid ? (
                <>
                  <Text.$ type="secondary" italic>
                    *If the customer would like to pay with a new credit card, you can send them the request link
                    below.*
                  </Text.$>
                  <NotificationsFields
                    isStandalone={true}
                    actionTitle="Request Card Info"
                    onSubmit={requestCreditCard}
                    isLoading={isRequestSending}
                    initialValues={initialValues}
                    emailOptions={emailOptions}
                    smsOptions={smsOptions}
                  />
                </>
              ) : (
                <Text.$ type="danger">
                  Credit Card has expired - the request card link will be sent automatically when the booking is
                  confirmed.
                </Text.$>
              )}
            </>
          )}

          {showNoCardMessage && (
            <Text.$ type="danger">
              No credit card on file. The request card link will be sent automatically when the booking is confirmed.
            </Text.$>
          )}
        </Space.$>
      )}
    </Section>
  );
});
